import {
  Assignment,
  Description,
  FitnessCenter,
  LocalHospital,
} from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ErrorIcon from '@mui/icons-material/Error';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SaveIcon from '@mui/icons-material/Save';
import {
  Box,
  FormControl,
  ListSubheader,
  MenuItem,
  Paper,
  Popper,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { logError } from '@ptek/client-logger';
import { useWidgetContext, useWidgetEvents } from '@ptek/widget-core';
import React, {
  ComponentType,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { WIDGET_EVENTS } from '../constants/events';
import { AppContext } from '../context/AppContext';
import { iconStyles } from '../styles/iconStyles';
import {
  getLegacyTriggerStyles,
  templateDropdownStyles as styles,
} from '../styles/templateDropdownStyles';
import type { FormattedTemplate } from '../types/templates';
import type {
  AmbientScribeContext,
  TemplateDropdownAction,
  TemplateDropdownActionIcon,
} from '../types/widget';
import { FEATURE_FLAGS } from '../types/featureFlags';
import { isFeatureEnabled } from '../utils/feature-util';
import { FlexRow } from './LayoutBoxes';

const DROPDOWN_ACTION_ICONS: Record<
  TemplateDropdownActionIcon,
  ComponentType
> = {
  openInNew: OpenInNewIcon,
};

interface TemplateDropdownProps {
  isSaving?: boolean;
  lastSaved?: Date | null;
  saveError?: string | null;
}

const getIconForDocumentType = (documentType: string) => {
  switch (documentType) {
    case 'SOAP_NOTE':
    case 'PROGRESS_NOTE':
    case 'CONSULTATION':
    case 'ASSESSMENT':
      return <LocalHospital />;
    case 'DISCHARGE_SUMMARY':
      return <Assignment />;
    case 'EXERCISE_PLAN':
      return <FitnessCenter />;
    default:
      return <Description />;
  }
};

const compareTemplateNames = (a: FormattedTemplate, b: FormattedTemplate) =>
  (a.name ?? '').localeCompare(b.name ?? '', undefined, {
    numeric: true,
    sensitivity: 'base',
  });

const isBrandTemplate = (template: FormattedTemplate) =>
  template.isHostProvided || template.scope === 'brand';

export const TemplateDropdown: ComponentType<TemplateDropdownProps> = ({
  isSaving = false,
  lastSaved = null,
  saveError = null,
}) => {
  const { sessionModel, selectTemplate } = useContext(AppContext);
  const { emitToHost } = useWidgetEvents();
  const { widgetContext } = useWidgetContext<AmbientScribeContext>();
  const dropdownActions: TemplateDropdownAction[] =
    widgetContext.context?.templateDropdownActions ?? [];
  const templates = sessionModel.templates;
  const selectedTemplateId = sessionModel.selectedTemplateId;

  const [selectMenuOpen, setSelectMenuOpen] = useState(false);
  const [legacyMenuOpen, setLegacyMenuOpen] = useState(false);
  const legacyMenuCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const legacyMenuAnchorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    return () => {
      if (legacyMenuCloseTimeoutRef.current) {
        clearTimeout(legacyMenuCloseTimeoutRef.current);
      }
    };
  }, []);

  const { brandTemplates, customTemplates, legacyTemplates } = useMemo(() => {
    const regular = templates.filter((t) => !t.isLegacy);
    const brand = regular.filter(isBrandTemplate).sort(compareTemplateNames);
    const custom = regular
      .filter((template) => !isBrandTemplate(template))
      .sort(compareTemplateNames);
    const legacy = templates
      .filter((t) => t.isLegacy)
      .sort((a, b) => {
        const versionCompare = (b.version ?? '').localeCompare(
          a.version ?? '',
          undefined,
          { numeric: true }
        );
        if (versionCompare !== 0) return versionCompare;
        return (a.name ?? '').localeCompare(b.name ?? '');
      });
    return {
      brandTemplates: brand,
      customTemplates: custom,
      legacyTemplates: legacy,
    };
  }, [templates]);

  const handleTemplateSelect = useCallback(
    async (templateId: string) => {
      try {
        await selectTemplate(templateId);
        setLegacyMenuOpen(false);
        setSelectMenuOpen(false);
      } catch (error) {
        logError('TemplateDropdown', 'Error selecting template:', error);
      }
    },
    [selectTemplate]
  );

  const handleSelectOpen = useCallback(() => {
    setSelectMenuOpen(true);
  }, []);

  const handleSelectClose = useCallback(() => {
    setSelectMenuOpen(false);
    setLegacyMenuOpen(false);
  }, []);

  const handleLegacyMenuEnter = useCallback(() => {
    if (legacyMenuCloseTimeoutRef.current) {
      clearTimeout(legacyMenuCloseTimeoutRef.current);
      legacyMenuCloseTimeoutRef.current = null;
    }
    setLegacyMenuOpen(true);
  }, []);

  const handleLegacyMenuLeave = useCallback(() => {
    legacyMenuCloseTimeoutRef.current = setTimeout(() => {
      setLegacyMenuOpen(false);
    }, 100);
  }, []);

  const handleLegacyMenuKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === 'ArrowRight') {
      event.preventDefault();
      event.stopPropagation();
      setLegacyMenuOpen(true);
    } else if (event.key === 'Escape' || event.key === 'ArrowLeft') {
      event.preventDefault();
      event.stopPropagation();
      setLegacyMenuOpen(false);
    }
  }, []);

  const triggerDropdownAction = useCallback(
    (actionId: string) => {
      emitToHost(WIDGET_EVENTS.EMITTER_NAMES.REQUEST_TEMPLATE_DROPDOWN_ACTION, {
        actionId,
      });
      setLegacyMenuOpen(false);
      setSelectMenuOpen(false);
    },
    [emitToHost],
  );

  const handleDropdownActionClick = useCallback(
    (event: React.MouseEvent, actionId: string) => {
      event.stopPropagation();
      triggerDropdownAction(actionId);
    },
    [triggerDropdownAction],
  );

  const handleDropdownActionKeyDown = useCallback(
    (event: React.KeyboardEvent, actionId: string) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        event.stopPropagation();
        triggerDropdownAction(actionId);
      }
    },
    [triggerDropdownAction],
  );

  const renderEnhancedTemplateItemContent = useCallback(
    (template: (typeof templates)[0]) => (
      <>
        <Box sx={styles.templateItemIcon}>
          {getIconForDocumentType(template.documentType)}
        </Box>
        <Box sx={styles.templateItemContent}>
          <Typography variant='menuItemPrimary' sx={styles.templateItemName}>
            {template.name}
          </Typography>
          <Typography variant='menuItemSecondary' sx={styles.templateItemDescription}>
            {template.description}
          </Typography>
        </Box>
        {template.isNew && <Box sx={styles.newBadge}>New</Box>}
      </>
    ),
    []
  );

  const renderTemplateMenuItem = useCallback(
    (template: (typeof templates)[0]) => {
      if (!template.isDisabled || !template.disabledTooltip) {
        return (
          <MenuItem
            key={template.id}
            value={template.id}
            disabled={template.isDisabled}
            sx={styles.menuItem}
          >
            {renderEnhancedTemplateItemContent(template)}
          </MenuItem>
        );
      }

      return (
        <MenuItem key={template.id} value={template.id} disabled sx={styles.menuItem}>
          <Tooltip
            title={template.disabledTooltip}
            placement='bottom'
            slotProps={{
              popper: {
                sx: styles.tooltipPopper,
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [0, -28],
                    },
                  },
                ],
              },
            }}
          >
            <Box
              sx={styles.disabledMenuItemContentWrapper}
              data-testid={`disabled-template-tooltip-target-${template.id}`}
            >
              {renderEnhancedTemplateItemContent(template)}
            </Box>
          </Tooltip>
        </MenuItem>
      );
    },
    [renderEnhancedTemplateItemContent]
  );

  const handleLegacyTemplateClick = useCallback(
    (templateId: string) => {
      setSelectMenuOpen(false);
      setLegacyMenuOpen(false);
      handleTemplateSelect(templateId);
    },
    [handleTemplateSelect]
  );

  const renderLegacyTemplateMenuItem = useCallback(
    (template: (typeof templates)[0]) => (
      <MenuItem
        key={template.id}
        selected={selectedTemplateId === template.id}
        onClick={(e) => {
          e.stopPropagation();
          handleLegacyTemplateClick(template.id);
        }}
        sx={styles.menuItem}
      >
        {renderEnhancedTemplateItemContent(template)}
      </MenuItem>
    ),
    [renderEnhancedTemplateItemContent, handleLegacyTemplateClick, selectedTemplateId]
  );

  const renderEnhancedTemplateList = useCallback(() => {
    const features = widgetContext.context?.features;
    const showCustomTemplates =
      customTemplates.length > 0 &&
      isFeatureEnabled(features, FEATURE_FLAGS.CUSTOM_TEMPLATES);
    const hasRegularTemplates =
      brandTemplates.length > 0 || showCustomTemplates;

    return [
      ...(brandTemplates.length > 0
        ? [
            <ListSubheader key='brand-templates-header' sx={styles.groupHeader}>
              Brand Templates
            </ListSubheader>,
            ...brandTemplates.map((template) => renderTemplateMenuItem(template)),
          ]
        : []),
      ...(showCustomTemplates
        ? [
            <ListSubheader key='my-templates-header' sx={styles.groupHeader}>
              My Templates
            </ListSubheader>,
            ...customTemplates.map((template) => renderTemplateMenuItem(template)),
          ]
        : []),
      ...(legacyTemplates.length > 0
        ? [
            ...(hasRegularTemplates
              ? [<Box key='legacy-divider' sx={styles.divider} />]
              : []),
            <Box
              key='legacy-trigger'
              ref={legacyMenuAnchorRef}
              role='menuitem'
              tabIndex={0}
              onMouseEnter={handleLegacyMenuEnter}
              onMouseLeave={handleLegacyMenuLeave}
              onKeyDown={handleLegacyMenuKeyDown}
              aria-haspopup='true'
              aria-expanded={legacyMenuOpen}
              sx={getLegacyTriggerStyles(legacyMenuOpen)}
            >
              <Typography sx={styles.legacyTriggerText}>
                Legacy Templates
              </Typography>
              <ChevronRightIcon sx={styles.chevronIcon} />
            </Box>,
            <Popper
              key='legacy-popper'
              open={legacyMenuOpen}
              anchorEl={legacyMenuAnchorRef.current}
              placement='right-start'
              sx={{ zIndex: 1000002 }}
              modifiers={[
                { name: 'offset', options: { offset: [0, -8] } },
                {
                  name: 'flip',
                  enabled: true,
                  options: {
                    fallbackPlacements: [
                      'left-start',
                      'right-end',
                      'left-end',
                      'bottom-start',
                      'top-start',
                    ],
                  },
                },
                {
                  name: 'preventOverflow',
                  enabled: true,
                  options: { boundary: 'viewport', altAxis: true, padding: 8 },
                },
              ]}
            >
              <Paper
                role='menu'
                variant='dropdown'
                onMouseEnter={handleLegacyMenuEnter}
                onMouseLeave={handleLegacyMenuLeave}
                sx={styles.legacySubmenuPaper}
              >
                {legacyTemplates.map(renderLegacyTemplateMenuItem)}
              </Paper>
            </Popper>,
          ]
        : []),
    ];
  }, [
    brandTemplates,
    customTemplates,
    legacyTemplates,
    renderTemplateMenuItem,
    handleLegacyMenuEnter,
    handleLegacyMenuLeave,
    handleLegacyMenuKeyDown,
    legacyMenuOpen,
    renderLegacyTemplateMenuItem,
    widgetContext,
  ]);

  return (
    <Box sx={styles.container}>
      <Typography variant='formLabel'>Template</Typography>

      <FormControl sx={styles.formControl}>
        <Select
          value={selectedTemplateId ?? ''}
          onChange={(event) => handleTemplateSelect(event.target.value)}
          open={selectMenuOpen}
          onOpen={handleSelectOpen}
          onClose={handleSelectClose}
          displayEmpty
          IconComponent={KeyboardArrowDownIcon}
          sx={styles.select}
          MenuProps={{
            PaperProps: { variant: 'dropdown', sx: styles.menuPaper },
            MenuListProps: { sx: styles.menuList },
            sx: styles.menuBackdrop,
          }}
          renderValue={(selected) => {
            if (!selected) {
              return (
                <Typography variant='placeholder' sx={styles.placeholderText}>
                  Select Template...
                </Typography>
              );
            }

            const template = templates.find((t) => t.id === selected);
            return (
              <Typography variant='body2' sx={styles.selectedValueText}>
                {template?.name || 'Select Template...'}
              </Typography>
            );
          }}
        >
          {selectedTemplateId &&
            !templates.some((t) => t.id === selectedTemplateId) && (
              <MenuItem
                key={selectedTemplateId}
                value={selectedTemplateId}
                sx={styles.hiddenMenuItem}
              >
                {selectedTemplateId}
              </MenuItem>
            )}
          {templates.length === 0 ? (
            <MenuItem key='no-templates' disabled>
              <Typography variant='body2'>
                No templates available
              </Typography>
            </MenuItem>
          ) : (
            renderEnhancedTemplateList()
          )}
          {dropdownActions.length > 0 && [
            <Box
              key='template-dropdown-actions-divider'
              sx={styles.divider}
            />,
            ...dropdownActions.map((action) => {
              const ActionIcon = action.icon
                ? DROPDOWN_ACTION_ICONS[action.icon]
                : null;
              return (
                <Box
                  key={`template-dropdown-action-${action.id}`}
                  role='menuitem'
                  tabIndex={0}
                  data-testid={`template-dropdown-action-${action.id}`}
                  onClick={(event) => handleDropdownActionClick(event, action.id)}
                  onKeyDown={(event) =>
                    handleDropdownActionKeyDown(event, action.id)
                  }
                  sx={{
                    ...styles.legacyTrigger,
                    justifyContent: 'flex-start',
                  }}
                >
                  {ActionIcon && (
                    <Box sx={styles.templateItemIcon}>
                      <ActionIcon />
                    </Box>
                  )}
                  <Typography sx={styles.legacyTriggerText}>
                    {action.label}
                  </Typography>
                </Box>
              );
            }),
          ]}
        </Select>
      </FormControl>

      {/* Status Display */}
      <FlexRow sx={styles.statusContainer}>
        {isSaving && (
          <Typography variant='statusText' sx={styles.statusText}>
            <SaveIcon sx={iconStyles.md} />
            Saving...
          </Typography>
        )}
        {saveError && !isSaving && (
          <Typography variant='statusText' sx={styles.statusTextError}>
            <ErrorIcon sx={iconStyles.md} />
            Save failed
          </Typography>
        )}
        {lastSaved && !isSaving && !saveError && (
          <Typography variant='statusText' sx={styles.statusText}>
            <CheckCircleIcon sx={iconStyles.md} color='success' />
            <span>
              Saved{' '}
              {lastSaved.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          </Typography>
        )}
      </FlexRow>
    </Box>
  );
};
